import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SlideToConfirm from '../components/SlideToConfirm';
import '../styles/Dashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const LONG_PRESS_MS = 550;

function Dashboard({ user }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myStatus, setMyStatus] = useState('');
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [attendanceMsg, setAttendanceMsg] = useState('');
  const [contextMenu, setContextMenu] = useState(null); // { x, y, id, name }
  const [confirmTarget, setConfirmTarget] = useState(null); // { id, name }
  const longPressTimer = React.useRef(null);

  const roles = user?.roles || [];
  const hasRole = (...r) => r.some(role => roles.includes(role));
  const canManageAttendance = hasRole('attendance_manager', 'admin');

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/attendance/today_summary/`);
      setSummary(response.data);
      const status = response.data.my_status;
      setMyStatus(status === 'attending' || status === 'not_attending' ? status : '');
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReclassify = async (attendanceId, newStatus) => {
    try {
      await axios.post(`${API_BASE_URL}/attendance/${attendanceId}/reclassify/`, { status: newStatus });
      fetchSummary();
    } catch (error) {
      console.error('Error reclassifying:', error);
    }
  };

  const closeContextMenu = () => setContextMenu(null);

  const openContextMenu = (e, member) => {
    if (!canManageAttendance) return;
    e.preventDefault();
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    setContextMenu({ x, y, id: member.id, name: member.name });
  };

  const handleTouchStart = (e, member) => {
    if (!canManageAttendance) return;
    const touch = e.touches?.[0];
    const x = touch?.clientX ?? 0;
    const y = touch?.clientY ?? 0;
    longPressTimer.current = setTimeout(() => {
      setContextMenu({ x, y, id: member.id, name: member.name });
    }, LONG_PRESS_MS);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleRequestNotAttending = () => {
    if (!contextMenu) return;
    setConfirmTarget({ id: contextMenu.id, name: contextMenu.name });
    setContextMenu(null);
  };

  const handleConfirmNotAttending = async () => {
    if (!confirmTarget) return;
    await handleReclassify(confirmTarget.id, 'not_attending');
    setConfirmTarget(null);
  };

  useEffect(() => {
    if (!contextMenu) return undefined;
    const handleClickAway = () => closeContextMenu();
    document.addEventListener('click', handleClickAway);
    document.addEventListener('contextmenu', handleClickAway);
    return () => {
      document.removeEventListener('click', handleClickAway);
      document.removeEventListener('contextmenu', handleClickAway);
    };
  }, [contextMenu]);

  const handleSaveMyAttendance = async () => {
    if (!myStatus) return;
    setSavingAttendance(true);
    setAttendanceMsg('');
    try {
      await axios.post(`${API_BASE_URL}/attendance/set_mine/`, { status: myStatus });
      setAttendanceMsg('Saved.');
      fetchSummary();
    } catch (error) {
      setAttendanceMsg(error.response?.data?.error || 'Failed to save.');
    } finally {
      setSavingAttendance(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <h2>Dashboard</h2>

      {summary?.poll_active && (
        <div className="my-attendance-section">
          <h3>My attendance today:</h3>
          <div className="my-attendance-controls">
            <select value={myStatus} onChange={(e) => setMyStatus(e.target.value)}>
              <option value="">-- Select --</option>
              <option value="attending">Attending</option>
              <option value="not_attending">Not Attending</option>
            </select>
            <button
              className="btn-save"
              disabled={!myStatus || savingAttendance}
              onClick={handleSaveMyAttendance}
            >
              {savingAttendance ? 'Saving...' : 'Save'}
            </button>
          </div>
          {attendanceMsg && <p className="attendance-msg">{attendanceMsg}</p>}
        </div>
      )}

      <div className="summary-section">
        <h3>Today's Attendance Summary</h3>
        {summary ? (
          <div className="summary-grid">
            <div className="summary-card attending">
              <span className="summary-label">Attending</span>
              <span className="summary-value">{summary.attending}</span>
            </div>
            <div className="summary-card not-attending">
              <span className="summary-label">Not Attending</span>
              <span className="summary-value">{summary.not_attending}</span>
            </div>
            <div className="summary-card undetermined">
              <span className="summary-label">Undetermined</span>
              <span className="summary-value">{summary.undetermined}</span>
            </div>
            <div className="summary-card no-response">
              <span className="summary-label">No Response</span>
              <span className="summary-value">{summary.no_response}</span>
            </div>
          </div>
        ) : (
          <p>No attendance data for today.</p>
        )}
      </div>

      {hasRole('message_reader', 'message_sender', 'broadcast_sender', 'attendance_manager', 'admin') && summary && (
        <div className="attending-section">
          <h3>Attending Members</h3>
          {summary.attending_members && summary.attending_members.length > 0 ? (
            <ul className="attending-list">
              {summary.attending_members.map((member) => (
                <li
                  key={member.id}
                  className={`attending-member${canManageAttendance ? ' manageable' : ''}`}
                  onContextMenu={(e) => openContextMenu(e, member)}
                  onTouchStart={(e) => handleTouchStart(e, member)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                >
                  {member.name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-data">No confirmed attendees yet.</p>
          )}
        </div>
      )}

      {contextMenu && (
        <div
          className="attendance-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="attendance-context-menu-title">{contextMenu.name}</div>
          <button
            type="button"
            className="attendance-context-menu-item danger"
            onClick={handleRequestNotAttending}
          >
            Mark as Not Attending
          </button>
        </div>
      )}

      {confirmTarget && (
        <div className="modal-overlay" onClick={() => setConfirmTarget(null)}>
          <div className="modal modal-danger" onClick={(e) => e.stopPropagation()}>
            <h3>Mark as Not Attending</h3>
            <p>
              Slide to confirm changing <strong>{confirmTarget.name}</strong>'s status to
              "Not Attending". This action requires confirmation to prevent accidental changes.
            </p>
            <SlideToConfirm label="Slide to confirm" onConfirm={handleConfirmNotAttending} />
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setConfirmTarget(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {hasRole('attendance_manager', 'admin') && summary?.undetermined_list?.length > 0 && (
        <div className="reclassify-section">
          <h3>Review Undetermined Responses</h3>
          <div className="reclassify-list">
            {summary.undetermined_list.map((record) => (
              <div key={record.id} className="reclassify-item">
                <span className="reclassify-name">{record.user_name}</span>
                <div className="reclassify-actions">
                  <button
                    className="btn-attending"
                    onClick={() => handleReclassify(record.id, 'attending')}
                  >
                    Attending
                  </button>
                  <button
                    className="btn-not-attending"
                    onClick={() => handleReclassify(record.id, 'not_attending')}
                  >
                    Not Attending
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
